import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLoader } from "../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import AIModelService from "../../../service/admin/aiModelService";
import ExcelTable from "../../../component/library/excelTable";
import { FaSearch, FaEdit, FaTrash, FaPlay, FaSync, FaPlus, FaAws, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { getAuthLocalStorage } from "../../../storage/localStorage";
import "./style.css";

export default function AIManagement() {
  const navigate = useNavigate();
  const loader = useLoader();
  const message = useMessage();

  const [activeTab, setActiveTab] = useState("models");
  const [models, setModels] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [usageLogs, setUsageLogs] = useState([]);
  const [usageStats, setUsageStats] = useState(null);
  const [aiStatus, setAiStatus] = useState(null);
  const [bedrockStatus, setBedrockStatus] = useState({ configured: false, loading: true });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterProvider, setFilterProvider] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [logsPagination, setLogsPagination] = useState({ page: 1, limit: 50, total: 0 });

  const authData = getAuthLocalStorage();
  const currentUserId = authData?.loginHeader || null;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === "usage") {
      fetchUsageData();
    }
  }, [activeTab, logsPagination.page]);

  const fetchData = async () => {
    setLoading(true);
    loader.showLoading(true);
    try {
      const [modelsRes, promptsRes, statusRes, bedrockRes] = await Promise.all([
        AIModelService.getAllModels(),
        AIModelService.getAllPrompts(),
        AIModelService.getAIStatus(),
        AIModelService.getBedrockStatus(),
      ]);
      if (modelsRes?.status?.code === 200) setModels(modelsRes.data?.models || []);
      if (promptsRes?.status?.code === 200) setPrompts(promptsRes.data?.prompts || []);
      if (statusRes?.status?.code === 200) setAiStatus(statusRes.data);
      if (bedrockRes?.status?.code === 200) setBedrockStatus({ ...bedrockRes.data, loading: false });
      else setBedrockStatus({ configured: false, loading: false });
    } catch (err) {
      message.showToast("Failed to load AI data", ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
      setLoading(false);
    }
  };

  const fetchUsageData = async () => {
    setLoading(true);
    try {
      const params = { page: logsPagination.page, limit: logsPagination.limit };
      if (filterProvider) params.provider = filterProvider;
      if (filterStatus) params.status = filterStatus;

      const [logsRes, statsRes] = await Promise.all([
        AIModelService.getUsageLogs(params),
        AIModelService.getUsageStats(),
      ]);
      if (logsRes?.status?.code === 200) {
        setUsageLogs(logsRes.data?.logs || []);
        setLogsPagination(prev => ({ ...prev, ...logsRes.data?.pagination }));
      }
      if (statsRes?.status?.code === 200) {
        setUsageStats(statsRes.data);
      }
    } catch (err) {
      message.showToast("Failed to load usage data", ToastTypes.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteModel = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this model?")) return;
    loader.showLoading(true);
    try {
      const res = await AIModelService.deleteModel(id);
      if (res?.status?.code === 200) {
        message.showToast("Model deleted", ToastTypes.SUCCESS);
        fetchData();
      } else {
        message.showToast(res?.error?.message || "Delete failed", ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast("Delete failed", ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
    }
  };

  const handleDeletePrompt = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this prompt?")) return;
    loader.showLoading(true);
    try {
      const res = await AIModelService.deletePrompt(id);
      if (res?.status?.code === 200) {
        message.showToast("Prompt deleted", ToastTypes.SUCCESS);
        fetchData();
      } else {
        message.showToast(res?.error?.message || "Delete failed", ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast("Delete failed", ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
    }
  };

  const handleTestModel = async (id, e) => {
    e.stopPropagation();
    loader.showLoading(true);
    try {
      const res = await AIModelService.testModel(id);
      if (res?.status?.code === 200) {
        message.showToast("Test successful", ToastTypes.SUCCESS);
      } else {
        message.showToast(res?.error?.message || "Test failed", ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast("Test failed", ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
    }
  };

  const filteredModels = useMemo(() => models.filter((m) => {
    const matchSearch = !searchTerm || m.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) || m.modelId?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchProvider = !filterProvider || m.provider === filterProvider;
    const matchStatus = !filterStatus || (filterStatus === "active" && m.isActive) || (filterStatus === "inactive" && !m.isActive);
    return matchSearch && matchProvider && matchStatus;
  }), [models, searchTerm, filterProvider, filterStatus]);

  const filteredPrompts = useMemo(() => prompts.filter((p) => {
    const matchSearch = !searchTerm || p.name?.toLowerCase().includes(searchTerm.toLowerCase()) || p.slug?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = !filterStatus || (filterStatus === "active" && p.isActive) || (filterStatus === "inactive" && !p.isActive);
    return matchSearch && matchStatus;
  }), [prompts, searchTerm, filterStatus]);

  const getProviderBadge = (provider) => {
    const colors = { openai: "#10a37f", deepseek: "#6366f1", gemini: "#4285f4", bedrock: "#ff9900" };
    return <span className="aim-provider-badge" style={{ background: colors[provider] || "#64748b" }}>{provider?.toUpperCase()}</span>;
  };

  const getStatusBadge = (isActive) => (
    <span className={`aim-status-badge ${isActive ? "aim-status-active" : "aim-status-inactive"}`}>
      {isActive ? "Active" : "Inactive"}
    </span>
  );

  const getLogStatusBadge = (status) => {
    const styles = {
      success: { bg: "#d1fae5", color: "#047857" },
      error: { bg: "#fee2e2", color: "#b91c1c" },
      timeout: { bg: "#fef3c7", color: "#b45309" },
    };
    const s = styles[status] || styles.error;
    return <span className="aim-status-badge" style={{ background: s.bg, color: s.color }}>{status?.toUpperCase()}</span>;
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("en-IN", { 
      day: "2-digit", month: "short", year: "numeric", 
      hour: "2-digit", minute: "2-digit" 
    });
  };

  const modelColumns = [
    { key: "provider", label: "Provider", width: 100, render: (v) => getProviderBadge(v) },
    { key: "displayName", label: "Display Name", width: 180, render: (v) => <span className="aim-name">{v}</span> },
    { key: "modelId", label: "Model ID", width: 220, render: (v) => <code className="aim-code">{v}</code> },
    { key: "maxTokens", label: "Max Tokens", width: 100, render: (v) => v?.toLocaleString() || "-" },
    { key: "temperature", label: "Temp", width: 70, render: (v) => v ?? "-" },
    { key: "isActive", label: "Status", width: 90, render: (v) => getStatusBadge(v) },
    { key: "isDefault", label: "Default", width: 80, render: (v) => v ? <span className="aim-default-badge">Default</span> : "-" },
    {
      key: "actions", label: "Actions", width: 140,
      render: (_, row) => (
        <div className="aim-actions">
          <button className="aim-action-btn aim-action-edit" onClick={(e) => { e.stopPropagation(); navigate(`/admin/ai-management/models/${row._id}`); }} title="Edit"><FaEdit /></button>
          {row.provider === "bedrock" && <button className="aim-action-btn aim-action-test" onClick={(e) => handleTestModel(row._id, e)} title="Test"><FaPlay /></button>}
          <button className="aim-action-btn aim-action-delete" onClick={(e) => handleDeleteModel(row._id, e)} title="Delete"><FaTrash /></button>
        </div>
      ),
    },
  ];

  const promptColumns = [
    { key: "name", label: "Name", width: 180, render: (v) => <span className="aim-name">{v}</span> },
    { key: "slug", label: "Slug", width: 200, render: (v) => <code className="aim-code">{v}</code> },
    { key: "page", label: "Page", width: 120 },
    { key: "action", label: "Action", width: 140 },
    { key: "assignedModel", label: "Model", width: 100, render: (v) => v ? getProviderBadge(v.provider) : <span className="aim-na">-</span> },
    { key: "responseFormat", label: "Format", width: 80, render: (v) => <span className="aim-format-badge">{v?.toUpperCase()}</span> },
    { key: "isActive", label: "Status", width: 90, render: (v) => getStatusBadge(v) },
    {
      key: "actions", label: "Actions", width: 100,
      render: (_, row) => (
        <div className="aim-actions">
          <button className="aim-action-btn aim-action-edit" onClick={(e) => { e.stopPropagation(); navigate(`/admin/ai-management/prompts/${row._id}`); }} title="Edit"><FaEdit /></button>
          <button className="aim-action-btn aim-action-delete" onClick={(e) => handleDeletePrompt(row._id, e)} title="Delete"><FaTrash /></button>
        </div>
      ),
    },
  ];

  const usageColumns = [
    { key: "createdAt", label: "Date", width: 150, render: (v) => formatDate(v) },
    { key: "provider", label: "Provider", width: 100, render: (v) => getProviderBadge(v) },
    { key: "modelId", label: "Model", width: 180, render: (v) => <code className="aim-code">{v?.split('.').pop() || v}</code> },
    { key: "requestType", label: "Type", width: 100, render: (v) => <span className="aim-format-badge">{v?.toUpperCase()}</span> },
    { key: "page", label: "Page", width: 120, render: (v) => v || "-" },
    { key: "totalTokens", label: "Tokens", width: 90, render: (v) => v?.toLocaleString() || "0" },
    { key: "estimatedCost", label: "Est. Cost", width: 80, render: (v) => `$${(v || 0).toFixed(4)}` },
    { key: "responseTime", label: "Time", width: 80, render: (v) => `${v || 0}ms` },
    { key: "status", label: "Status", width: 90, render: (v) => getLogStatusBadge(v) },
    { key: "userName", label: "User", width: 120, render: (v) => v || "-" },
  ];

  const getTableData = () => {
    if (activeTab === "models") return filteredModels;
    if (activeTab === "prompts") return filteredPrompts;
    return usageLogs;
  };

  const getTableColumns = () => {
    if (activeTab === "models") return modelColumns;
    if (activeTab === "prompts") return promptColumns;
    return usageColumns;
  };

  const getTableTitle = () => {
    if (activeTab === "models") return "AI Models";
    if (activeTab === "prompts") return "AI Prompts";
    return "AI Management";
  };

  const toolbarLeftActions = (
    <div className="aim-toolbar-left">
      <div className="aim-tabs">
        <button className={activeTab === "models" ? "active" : ""} onClick={() => { setActiveTab("models"); setSearchTerm(""); setFilterProvider(""); setFilterStatus(""); }}>Models</button>
        <button className={activeTab === "prompts" ? "active" : ""} onClick={() => { setActiveTab("prompts"); setSearchTerm(""); setFilterProvider(""); setFilterStatus(""); }}>Prompts</button>
        <button className={activeTab === "usage" ? "active" : ""} onClick={() => { setActiveTab("usage"); setSearchTerm(""); setFilterProvider(""); setFilterStatus(""); }}>Usage Logs</button>
      </div>
      {activeTab !== "usage" && (
        <div className="aim-search-box">
          <FaSearch className="aim-search-icon" />
          <input type="text" className="aim-search-input" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
      )}
      {(activeTab === "models" || activeTab === "usage") && (
        <select className="aim-filter-select" value={filterProvider} onChange={(e) => { setFilterProvider(e.target.value); if (activeTab === "usage") fetchUsageData(); }}>
          <option value="">All Providers</option>
          <option value="bedrock">Bedrock</option>
          <option value="openai">OpenAI</option>
          <option value="deepseek">DeepSeek</option>
          <option value="gemini">Gemini</option>
        </select>
      )}
      {activeTab === "usage" ? (
        <select className="aim-filter-select" value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); fetchUsageData(); }}>
          <option value="">All Status</option>
          <option value="success">Success</option>
          <option value="error">Error</option>
          <option value="timeout">Timeout</option>
        </select>
      ) : (
        <select className="aim-filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      )}
      <div className="aim-total">
        Total: <span>{activeTab === "models" ? filteredModels.length : activeTab === "prompts" ? filteredPrompts.length : logsPagination.total}</span>
      </div>
      {/* Usage Stats - inline in toolbar */}
      {activeTab === "usage" && usageStats?.overall && (
        <div className="aim-inline-stats">
          <span className="aim-inline-stat"><strong>{usageStats.overall.totalCalls || 0}</strong> calls</span>
          <span className="aim-inline-stat"><strong>{(usageStats.overall.totalTokens || 0).toLocaleString()}</strong> tokens</span>
          <span className="aim-inline-stat"><strong>${(usageStats.overall.totalCost || 0).toFixed(4)}</strong></span>
          <span className="aim-inline-stat success"><strong>{usageStats.overall.successCount || 0}</strong> ✓</span>
          <span className="aim-inline-stat error"><strong>{usageStats.overall.errorCount || 0}</strong> ✗</span>
        </div>
      )}
      {/* AWS Bedrock Status */}
      {activeTab !== "usage" && (
        <div className={`aim-bedrock-status ${bedrockStatus.configured ? "connected" : "disconnected"}`} title={bedrockStatus.configured ? `AWS Bedrock Connected (${bedrockStatus.region})` : "AWS Bedrock Not Configured"}>
          <FaAws className="aim-aws-icon" />
          {bedrockStatus.configured ? <FaCheckCircle className="aim-status-icon" /> : <FaTimesCircle className="aim-status-icon" />}
        </div>
      )}
      {activeTab !== "usage" && aiStatus && (
        <div className="aim-providers-status">
          {Object.entries(aiStatus.providers || {}).map(([p, c]) => (
            <span key={p} className={`aim-provider-dot ${c ? "on" : "off"}`} title={`${p}: ${c ? "Configured" : "Not Configured"}`}>{p.slice(0, 1).toUpperCase()}</span>
          ))}
        </div>
      )}
    </div>
  );

  const toolbarRightActions = (
    <div className="aim-toolbar-right">
      <button className="aim-refresh-btn" onClick={activeTab === "usage" ? fetchUsageData : fetchData} title="Refresh">
        <FaSync className={loading ? "aim-spin" : ""} />
      </button>
      {activeTab !== "usage" && (
        <button className="aim-add-btn" onClick={() => navigate(activeTab === "models" ? "/admin/ai-management/models/new" : "/admin/ai-management/prompts/new")}>
          <FaPlus /> Add {activeTab === "models" ? "Model" : "Prompt"}
        </button>
      )}
    </div>
  );

  return (
    <div className="aim-page">
      <div className="aim-table-section">
        <ExcelTable
          key={`aim-table-${activeTab}`}
          data={getTableData()}
          columns={getTableColumns()}
          isLoading={loading}
          loadingText="Loading AI data..."
          showToolbarExport={activeTab === "usage"}
          showToolbarFilter={false}
          showToolbarColumns={true}
          showToolbarFullscreen={true}
          toolbarLeftActions={toolbarLeftActions}
          toolbarRightActions={toolbarRightActions}
          selectable={false}
          sortable={true}
          resizable={true}
          showToolbar={true}
          title={getTableTitle()}
          onRowClick={activeTab !== "usage" ? (row) => navigate(activeTab === "models" ? `/admin/ai-management/models/${row._id}` : `/admin/ai-management/prompts/${row._id}`) : undefined}
          userId={currentUserId}
        />
      </div>
    </div>
  );
}
